// src/calculation.ts

export interface Expense {
  description: string;
  amount: number;
  paidBy: string;
  isTreat: boolean;
  participants: string[];
}

export interface Settlement {
  from: string;
  to: string;
  amount: number;
}

export interface CalculationResults {
  totalOwedByPerson: Record<string, number>;
  owedBreakdown: Record<string, { description: string; amount: number }[]>; // New field
  settlements: Settlement[];
  totalSpent: number;
  treats: Expense[];
}

export function calculate(participants: string[], expenses: Expense[]): CalculationResults {
  if (participants.length === 0) {
    return { totalOwedByPerson: {}, owedBreakdown: {}, settlements: [], totalSpent: 0, treats: [] };
  }

  const totalSpent = expenses.filter(e => !e.isTreat).reduce((acc, expense) => acc + expense.amount, 0);
  const treats = expenses.filter(e => e.isTreat);

  // 1. Calculate total amount each person should have paid in the end.
  const totalOwedByPerson: Record<string, number> = participants.reduce((acc, person) => ({ ...acc, [person]: 0 }), {});
  const owedBreakdown: Record<string, { description: string; amount: number }[]> = participants.reduce((acc, person) => ({ ...acc, [person]: [] }), {});

  expenses.forEach(expense => {
    if (!expense.isTreat && expense.participants.length > 0) {
      const costPerPersonForExpense = expense.amount / expense.participants.length;
      expense.participants.forEach(participant => {
        if (totalOwedByPerson[participant] !== undefined) {
          totalOwedByPerson[participant] += costPerPersonForExpense;
          owedBreakdown[participant].push({ description: expense.description, amount: costPerPersonForExpense });
        }
      });
    }
  });

  // 2. "Gross Debts" Settlement Algorithm
  const debtMap = new Map<string, number>(); // JSON pairs preserve arbitrary names.

  // Create a map of all individual debts from each expense
  for (const expense of expenses) {
    if (expense.isTreat || expense.participants.length === 0) continue;

    const share = expense.amount / expense.participants.length;
    for (const participant of expense.participants) {
      if (participant === expense.paidBy) continue;
      
      const key = JSON.stringify([participant, expense.paidBy]);
      const currentDebt = debtMap.get(key) || 0;
      debtMap.set(key, currentDebt + share);
    }
  }

  // Simplify opposing debts (e.g., if A owes B and B owes A)
  const simplifiedDebts = new Map<string, number>();
  const processedKeys = new Set<string>();

  debtMap.forEach((amount, key) => {
    if (processedKeys.has(key)) return;

    const [from, to]: [string, string] = JSON.parse(key);
    const reverseKey = JSON.stringify([to, from]);
    const reverseAmount = debtMap.get(reverseKey);

    if (reverseAmount !== undefined) {
      if (amount > reverseAmount) {
        simplifiedDebts.set(key, amount - reverseAmount);
      } else if (reverseAmount > amount) {
        simplifiedDebts.set(reverseKey, reverseAmount - amount);
      }
      processedKeys.add(key);
      processedKeys.add(reverseKey);
    } else {
        simplifiedDebts.set(key, amount);
    }
  });

  // Convert the final map to the settlements array
  const settlements: Settlement[] = [];
  simplifiedDebts.forEach((amount, key) => {
    if (amount > 0.01) { // Ignore tiny amounts
      const [from, to]: [string, string] = JSON.parse(key);
      settlements.push({ from, to, amount });
    }
  });

  settlements.sort((a, b) => b.amount - a.amount); // Sort by amount descending

  return { totalOwedByPerson, owedBreakdown, settlements, totalSpent, treats };
}
